import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  Link,
  Typography
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useTranslation } from "react-i18next";
import ActionButton from "../../Button/ActionButton";
import useDocumentUpload, {
  validateFile
} from "../../../hooks/useDocumentUpload";
import { PaymentDocument, PaymentDocumentType } from "../../../types/payments";
import { KYC_ACCEPTED_FILE_TYPES } from "../../../constants";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

/** A file picked in the browser that has not been sent to the API yet. */
interface StagedFile {
  file: File;
  /** Object URL, used for the local preview and revoked when it is dropped. */
  url: string;
}

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * One document slot: picks a file, previews it locally and only uploads it on
 * an explicit confirmation.
 *
 * Picking a file no longer sends it. The file is staged, previewed, and travels
 * to the API only when the professional confirms — a KYC document that goes to
 * the wrong slot has to be deleted on the backend to be corrected, so the
 * second look is worth the extra click. Nothing is deleted while a file is
 * staged either: replacing a stored document removes the old one only once the
 * new one is actually being sent.
 *
 * The mobile app restricts some of these to the camera only. That restriction
 * does not carry over to a browser, so every slot accepts an image or a PDF.
 */
export default function DocumentUploadField({
  configId,
  documentType,
  idGroup,
  document,
  onChange,
  readOnly = false
}: {
  configId: string;
  documentType: PaymentDocumentType;
  idGroup: string;
  document?: PaymentDocument;
  onChange: () => void;
  /**
   * Shows what is on file without offering to change it — for a configuration
   * whose KYC stage has closed. The API answers such an upload with a 200 and a
   * policy that registers nothing, so the file would reach storage and the
   * document would never exist.
   */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, remove, progress } = useDocumentUpload();
  const [staged, setStaged] = useState<StagedFile | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Counts nested enter/leave pairs instead of holding a boolean. Dragging over
   * a child element fires `dragleave` on the parent, which would clear the
   * highlight while the pointer is still inside the slot.
   */
  const [dragDepth, setDragDepth] = useState(0);
  const dragging = dragDepth > 0;

  const label = t(`paymentDocumentType.${documentType}`);
  const uploaded = Boolean(document);

  // Revokes the object URL of the file that was staged before this one, and of
  // the last one when the slot unmounts: they would leak otherwise.
  useEffect(() => {
    if (!staged) return;
    return () => URL.revokeObjectURL(staged.url);
  }, [staged]);

  const stageFile = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(t(`paymentDocuments.${validationError}`));
        return;
      }
      setError(null);
      setStaged({ file, url: URL.createObjectURL(file) });
    },
    [t]
  );

  const discardStaged = useCallback(() => {
    setStaged(null);
    setError(null);
  }, []);

  const onFileSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Let the same file be picked again after an error.
      event.target.value = "";
      if (file) stageFile(file);
    },
    [stageFile]
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setDragDepth(0);
      // An upload is already running for this slot; a second file would race it.
      if (busy) return;
      // Only the first file: each slot holds exactly one document.
      const file = event.dataTransfer.files?.[0];
      if (file) stageFile(file);
    },
    [busy, stageFile]
  );

  /**
   * Without preventing the default on dragover the browser opens the dropped
   * file in the tab, navigating away from a half-filled form.
   */
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const onDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragDepth(depth => depth + 1);
  }, []);

  const onDragLeave = useCallback(() => {
    setDragDepth(depth => Math.max(0, depth - 1));
  }, []);

  const confirmUpload = useCallback(async () => {
    if (!staged) return;
    setError(null);
    setBusy(true);
    try {
      // Replacing means dropping the previous document first, so the
      // configuration never ends up with two files for the same slot. It only
      // happens here, once the replacement is on its way.
      if (document) {
        await remove(configId, document.id);
      }
      await upload({ configId, documentType, idGroup, file: staged.file });
      setStaged(null);
      onChange();
    } catch {
      setError(t("paymentDocuments.uploadError"));
      onChange();
    } finally {
      setBusy(false);
    }
  }, [
    configId,
    document,
    documentType,
    idGroup,
    onChange,
    remove,
    staged,
    t,
    upload
  ]);

  const onRemove = useCallback(async () => {
    if (!document) return;
    setError(null);
    setBusy(true);
    try {
      await remove(configId, document.id);
      onChange();
    } catch {
      setError(t("paymentDocuments.removeError"));
    } finally {
      setBusy(false);
    }
  }, [configId, document, onChange, remove, t]);

  const statusIcon = staged ? (
    <UploadFileIcon
      sx={{ color: semanticColors.status.inProgress, fontSize: 20 }}
    />
  ) : uploaded ? (
    <CheckCircleIcon
      sx={{ color: semanticColors.status.completed, fontSize: 20 }}
    />
  ) : (
    <UploadFileIcon
      sx={{ color: semanticColors.text.secondary, fontSize: 20 }}
    />
  );

  return (
    <Box
      onDragEnter={readOnly ? undefined : onDragEnter}
      onDragOver={readOnly ? undefined : onDragOver}
      onDragLeave={readOnly ? undefined : onDragLeave}
      onDrop={readOnly ? undefined : onDrop}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1.5,
        px: 1.5,
        borderRadius: "8px",
        // The slot only reads as a drop target while a file is over it, so the
        // list stays calm the rest of the time. The separator between slots is
        // kept, and set after the shorthand so it is not overridden by it.
        border: dragging
          ? `1px dashed ${semanticColors.border.focus}`
          : "1px dashed transparent",
        borderBottom: dragging
          ? `1px dashed ${semanticColors.border.focus}`
          : `1px solid ${semanticColors.border.light}`,
        backgroundColor: dragging ? semanticColors.highlight.low : "transparent"
      }}
    >
      {/* A grid rather than space-between: the drop hint and the buttons of
          every slot line up on the same two columns regardless of how long the
          document name is. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1fr) minmax(0, 200px) 240px"
          },
          alignItems: "center",
          columnGap: 2,
          rowGap: 1
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          {statusIcon}
          <Typography
            sx={{
              ...typography.body.l,
              color:
                uploaded || staged
                  ? semanticColors.text.primary
                  : semanticColors.text.secondary
            }}
          >
            {staged
              ? t("paymentDocuments.readyToSend", { label })
              : uploaded
              ? t("paymentDocuments.uploaded", { label })
              : t("paymentDocuments.add", { label })}
          </Typography>
        </Box>

        {/* An affordance is needed because a drop target is invisible until it
            is used; it doubles as the live prompt while a file is over it. */}
        {readOnly ? (
          <Box />
        ) : (
          <Typography
            sx={{
              ...typography.body.m,
              textAlign: { xs: "left", md: "center" },
              color: dragging
                ? semanticColors.action.primary
                : semanticColors.text.subtle
            }}
          >
            {dragging
              ? t("paymentDocuments.dropActive")
              : t("paymentDocuments.dropHint")}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "flex-start", md: "flex-end" },
            gap: 1
          }}
        >
          {readOnly ? null : staged ? (
            <>
              <ActionButton
                label={t("paymentDocuments.send")}
                onClick={confirmUpload}
                loading={busy}
              />
              <ActionButton
                label={t("common.cancel")}
                onClick={discardStaged}
                disabled={busy}
                variant="secondary"
              />
            </>
          ) : (
            <>
              <ActionButton
                label={
                  uploaded
                    ? t("paymentDocuments.replaceFile")
                    : t("paymentDocuments.chooseFile")
                }
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                // One of many per-document pickers: the outline keeps the list
                // calm and leaves the gradient for the single confirming action.
                variant="secondary"
              />
              {uploaded && (
                <IconButton
                  onClick={onRemove}
                  disabled={busy}
                  aria-label={t("paymentDocuments.remove", { label })}
                >
                  <DeleteOutlineIcon
                    sx={{ color: semanticColors.status.error }}
                  />
                </IconButton>
              )}
            </>
          )}
        </Box>
      </Box>

      {staged && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1,
            borderRadius: "8px",
            backgroundColor: semanticColors.surface.containerLow
          }}
        >
          {staged.file.type === "application/pdf" ? (
            <PictureAsPdfIcon
              sx={{ color: semanticColors.text.secondary, fontSize: 40 }}
            />
          ) : (
            <Box
              component="img"
              src={staged.url}
              alt={staged.file.name}
              sx={{
                width: 64,
                height: 64,
                objectFit: "cover",
                borderRadius: "4px",
                border: `1px solid ${semanticColors.border.light}`
              }}
            />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                ...typography.body.m,
                color: semanticColors.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {staged.file.name} · {formatSize(staged.file.size)}
            </Typography>
            <Link
              href={staged.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ...typography.body.m }}
            >
              {t("paymentDocuments.openPreview")}
            </Link>
          </Box>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={KYC_ACCEPTED_FILE_TYPES}
        onChange={onFileSelected}
        hidden
      />

      {busy && (
        <LinearProgress
          variant={progress === null ? "indeterminate" : "determinate"}
          value={progress ?? 0}
        />
      )}
      {error && (
        <Typography
          sx={{ ...typography.body.m, color: semanticColors.status.error }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}
